"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { useWebMcpTool } from "@/lib/webmcp/react";

type PublicShareAffordanceLabels = {
  readonly share: string;
  readonly linkedin: string;
  readonly copied: string;
  readonly failed: string;
};

type PublicShareAffordanceProps = {
  readonly analyticsIdBase: string;
  readonly analyticsVariantKey?: string;
  readonly canonicalUrl: string;
  readonly labels: PublicShareAffordanceLabels;
};

type ShareStatus = "idle" | "copied" | "failed";

async function copyTextToClipboard(value: string): Promise<boolean> {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      // Fall through to the legacy copy path.
    }
  }

  if (typeof document === "undefined") {
    return false;
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "fixed";
  textarea.style.insetBlockStart = "-9999px";
  textarea.style.insetInlineStart = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();

  try {
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    textarea.remove();
  }
}

export default function PublicShareAffordance({
  analyticsIdBase,
  analyticsVariantKey = "control",
  canonicalUrl,
  labels,
}: PublicShareAffordanceProps) {
  const [status, setStatus] = useState<ShareStatus>("idle");
  const resetTimerRef = useRef<number | null>(null);
  const linkedInShareHref = useMemo(
    () => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(canonicalUrl)}`,
    [canonicalUrl],
  );
  const copyLabel = status === "copied" ? labels.copied : status === "failed" ? labels.failed : labels.share;

  useEffect(
    () => () => {
      if (resetTimerRef.current) {
        window.clearTimeout(resetTimerRef.current);
      }
    },
    [],
  );

  async function handleCopyClick() {
    const copied = await copyTextToClipboard(canonicalUrl);

    if (resetTimerRef.current) {
      window.clearTimeout(resetTimerRef.current);
    }

    setStatus(copied ? "copied" : "failed");
    resetTimerRef.current = window.setTimeout(() => setStatus("idle"), 1800);
    return copied;
  }

  const instance = analyticsIdBase.toLowerCase().replace(/[^a-z0-9_]/g, "_").slice(0, 80);
  useWebMcpTool({ action: "copy_canonical_link", instance, schema: z.strictObject({}), preview: () => canonicalUrl,
    execute: async () => ({ status: await handleCopyClick() ? "success" : "failure", uiState: "copy_attempted" }) });
  useWebMcpTool({ action: "open_linkedin_share", instance, schema: z.strictObject({}), preview: () => linkedInShareHref,
    execute: () => { window.location.assign(linkedInShareHref); return { status: "success", uiState: "share_page_requested", data: { published: false } }; } });

  return (
    <div className="cro-share" data-cro-share>
      <p className="redesign-card__eyebrow">{labels.share}</p>
      <div className="cro-share__actions" aria-label={labels.share}>
        <a
          className="cro-share__button"
          href={linkedInShareHref}
          target="_blank"
          rel="noopener noreferrer"
          data-cro-share-action="linkedin"
          data-analytics-component-family="cta"
          data-analytics-component-id={`${analyticsIdBase}.linkedin`}
          data-analytics-cta-id={`cta.${analyticsIdBase}.linkedin`}
          data-analytics-variant-key={analyticsVariantKey}
        >
          {labels.linkedin}
        </a>
        <button
          className="cro-share__button"
          type="button"
          onClick={handleCopyClick}
          data-cro-share-action="copy-link"
          data-analytics-component-family="cta"
          data-analytics-component-id={`${analyticsIdBase}.copy-link`}
          data-analytics-cta-id={`cta.${analyticsIdBase}.copy-link`}
          data-analytics-variant-key={analyticsVariantKey}
        >
          {copyLabel}
        </button>
      </div>
    </div>
  );
}
